import { Component, OnInit, OnDestroy } from "@angular/core";
import { DialogService } from "@progress/kendo-angular-dialog";
import { Constants } from "@/constants";
import { I18nService } from "@/core/services/i18nService";
import { ConfirmationDialogAction, ConfirmationDialogComponent, ConfirmationDialogResult } from "@/core/components/dialogs/confirmation-dialog/confirmation-dialog.component";
import { Router } from "@angular/router";
import { LoadingAnimationService } from '../../../../services/loading-animation.service';
import { AiService } from "@/api/service-index";
import { ChatCompletionDisplayModel, PortalAiChatRole } from "@/api/model-index";
import { LlmResponseService } from "@/core/services/llm-response.service";
import { marked } from "marked";
import { TextBoxService } from "@/core/services/textbox.service";
import { FormControl } from "@angular/forms";

type ChatMessageViewModel = ChatCompletionDisplayModel & {
    uiId: string;
    uiUseTypewriter?: boolean;
    uiRawContent?: string;
    uiRenderedContent?: string;
};

@Component({
    selector: 'ai',
    templateUrl: './ai.component.html',
    styleUrls: ['./ai.component.scss'],
    standalone: false
})
export class AIComponent implements OnInit, OnDestroy {
    public messageControl: FormControl<string | null> = new FormControl<string>('');
    public sending: boolean = false;
    public messages: ChatMessageViewModel[] = [];
    
    constructor(
        protected aiService: AiService, 
        protected dialogService: DialogService, 
        protected languageService: I18nService, 
        protected router: Router, 
        protected loadingAnimationService: LoadingAnimationService,
        protected llmResponseService: LlmResponseService,
        protected textBoxService: TextBoxService
    ) {
        const storedMessages = localStorage.getItem('chatMessages');
        if (storedMessages) {
            try {
                const parsed: ChatCompletionDisplayModel[] = JSON.parse(storedMessages);
                this.messages = parsed.map(message => this.createMessageFromStored(message));
                if (this.messages.length) {
                    this.persistMessages();
                }
            } catch (error) {
                console.error('Failed to parse stored chat messages', error);
                this.messages = [];
            }
        }
    }

    ngOnInit(): void {
        this.llmResponseService.setupScrollListener('container');
        this.scrollToBottom(100);
    }

    ngOnDestroy(): void {
        this.llmResponseService.cleanup();
    }

    public deleteChat() {
        let dialog = this.dialogService.open({
            content: ConfirmationDialogComponent,
            maxWidth: Constants.DIALOG.MAX_WIDTH,
            maxHeight: Constants.DIALOG.MAX_HEIGHT,
            width: Constants.DIALOG.WIDTH,
            title: this.languageService.instant("aichat.delete")
        });

        dialog.content.instance.content = this.languageService.instant("aichat.delete.dialog.message");

        dialog.result.subscribe(result => this.onDeleteChatConfirmation(<ConfirmationDialogResult>result));
    }

    protected onDeleteChatConfirmation(result: ConfirmationDialogResult) {
        if (result.action == ConfirmationDialogAction.Confirm) {
            localStorage.removeItem('chatMessages');
            this.messages = [];
        }
    }

    async sendMessage(): Promise<void> {
        if (!this.messageControl.value?.trim() || this.sending) {
            return;
        }

        this.sending = true;

        const userMessage = this.createUserMessage(this.messageControl.value.trim());
        this.messages.push(userMessage);

        const messageCopy = this.messageControl.value;
        this.messageControl.setValue('');
        this.scrollToBottom();

        // Remove previous limit reached notifications when user sends a new question
        this.messages = this.messages.filter(message => message.isLimitReached !== true);

        const typingIndicator = this.createTypingIndicator();
        this.messages.push(typingIndicator);

        const payload = this.buildApiMessages();

        let result;
        try {
            result = await this.aiService.apiAiSaveChatCompletionPost({ body: payload }).toPromise();
        } catch (error: any) {
            console.log("error", error);
            this.handleSendError(error, messageCopy);
            return;
        }

        this.removeTypingIndicator();

        this.sending = false;

        const assistantMessage = this.createAssistantMessage(result);

        this.messages.push(assistantMessage);

        this.scrollToBottom();
    }

    onAssistantTypewriterComplete(index: number): void {
        const message = this.messages[index];
        if (!message) {
            return;
        }

        if (message.uiUseTypewriter) {
            const finalContent = message.uiRenderedContent
                ?? this.renderMarkdown(message.uiRawContent ?? message.message ?? '');
            message.uiRenderedContent = finalContent;
            message.message = finalContent;
            message.uiUseTypewriter = false;
            this.persistMessages();
        }
    }

    onAssistantContentUpdate(index: number, htmlContent: string): void {
        const message = this.messages[index];
        if (!message) {
            return;
        }

        if (message.role === PortalAiChatRole.System) {
            message.uiRenderedContent = htmlContent;
        }
    }

    trackByMessage(_index: number, message: ChatMessageViewModel): string {
        return message.uiId;
    }

    private generateMessageId(): string {
        return `msg-${Math.random().toString(36).slice(2, 11)}-${Date.now().toString(36)}`;
    }

    private buildApiMessages(): ChatCompletionDisplayModel[] {
        return this.messages
            .filter(message => !message.isTyping)
            .map(message => this.toDataModel(message));
    }

    private removeTypingIndicator(): void {
        this.messages = this.messages.filter(message => !message.isTyping);
    }

    private handleSendError(error: any, messageCopy: string): void {
        this.removeTypingIndicator();

        // Remove the last user message to restore previous state
        if (this.messages.length && this.messages[this.messages.length - 1].role === PortalAiChatRole.User) {
            this.messages.pop();
        }

        if (error?.status === 429) {
            const rateLimitMessage = this.createRateLimitMessage();
            this.messages.push(rateLimitMessage);
            this.scrollToBottom(100);
        }

        this.messageControl.setValue(messageCopy);
        this.sending = false;
    }

    private scrollToBottom(delay: number = 0): void {
        setTimeout(() => {
            this.llmResponseService.forceScroll('chat-bottom');
        }, delay);
    }

    private persistMessages(): void {
        const storageMessages = this.messages
            .filter(message => !message.isTyping)
            .map(message => this.toDataModel(message));
        localStorage.setItem('chatMessages', JSON.stringify(storageMessages));
    }

    private createMessageFromStored(stored: ChatCompletionDisplayModel): ChatMessageViewModel {
        const message = this.cloneMessage(stored) as ChatMessageViewModel;
        message.uiId = this.generateMessageId();
        message.uiUseTypewriter = false;
        const originalContent = message.message ?? '';

        if (message.role === PortalAiChatRole.System) {
            if (this.containsHtml(originalContent)) {
                message.uiRawContent = this.stripHtml(originalContent);
                message.uiRenderedContent = originalContent;
                message.message = message.uiRawContent;
            } else {
                message.uiRawContent = originalContent;
                message.uiRenderedContent = this.renderMarkdown(originalContent);
                message.message = message.uiRawContent;
            }
        } else {
            message.uiRawContent = originalContent;
            message.uiRenderedContent = originalContent;
        }
        return message;
    }

    private createUserMessage(content: string): ChatMessageViewModel {
        const message = new ChatCompletionDisplayModel() as ChatMessageViewModel;
        message.uiId = this.generateMessageId();
        message.role = PortalAiChatRole.User;
        message.message = content;
        message.uiRawContent = content;
        message.uiUseTypewriter = false;
        return message;
    }

    private createTypingIndicator(): ChatMessageViewModel {
        const message = new ChatCompletionDisplayModel() as ChatMessageViewModel;
        message.uiId = this.generateMessageId();
        message.isTyping = true;
        message.role = PortalAiChatRole.System;
        message.uiUseTypewriter = false;
        return message;
    }

    private createAssistantMessage(result: ChatCompletionDisplayModel): ChatMessageViewModel {
        const message = this.cloneMessage(result) as ChatMessageViewModel;
        message.uiId = this.generateMessageId();
        message.uiUseTypewriter = true;
        const rawContent = result.message ?? '';
        message.uiRawContent = rawContent;
        message.uiRenderedContent = undefined;
        message.message = rawContent;
        return message;
    }

    private createRateLimitMessage(): ChatMessageViewModel {
        const message = new ChatCompletionDisplayModel() as ChatMessageViewModel;
        message.uiId = this.generateMessageId();
        message.role = PortalAiChatRole.System;
        message.isLimitReached = true;
        const content = this.languageService.instant("aichat.ratelimit.message");
        message.message = content;
        message.uiRawContent = content;
        message.uiUseTypewriter = false;
        return message;
    }

    private cloneMessage(source: ChatCompletionDisplayModel): ChatCompletionDisplayModel {
        const clone = new ChatCompletionDisplayModel();
        clone.isLimitReached = source.isLimitReached;
        clone.isTyping = source.isTyping;
        clone.message = source.message;
        clone.role = source.role;
        return clone;
    }

    private toDataModel(message: ChatMessageViewModel): ChatCompletionDisplayModel {
        const clone = new ChatCompletionDisplayModel();
        clone.isLimitReached = message.isLimitReached;
        clone.isTyping = message.isTyping;
        clone.role = message.role;
        clone.message = message.uiRawContent ?? message.message;
        return clone;
    }

    private containsHtml(content: string): boolean {
        return /<[a-z][\s\S]*>/i.test(content);
    }

    private renderMarkdown(content: string): string {
        try {
            return marked.parse(content) as string;
        } catch (error) {
            console.error('Failed to render markdown', error);
            return content;
        }
    }

    private stripHtml(content: string): string {
        const tempElement = document.createElement('div');
        tempElement.innerHTML = content;
        return tempElement.textContent || tempElement.innerText || '';
    }
}
